/**
 * SpinnerExamples.tsx
 * Comprehensive examples of HeroUI Spinner component
 * 
 * Showcases all HeroUI Spinner features using our shared components
 */

import React, { useState } from 'react';
import { PlaygroundPageLayout } from '../shared';
import { Spinner } from '@enfomentis/ui';
import { Card, CardBody, CardHeader } from '@enfomentis/ui';
import { Button } from '@enfomentis/ui';


export const SpinnerExamples: React.FC = () => {
  const [isLoading, setIsLoading] = useState(false);
  const [isProcessing, setIsProcessing] = useState(false);
  const [isAnalyzing, setIsAnalyzing] = useState(false);

  const simulateLoading = () => {
    setIsLoading(true);
    setTimeout(() => setIsLoading(false), 3000);
  };

  const simulateProcessing = () => {
    setIsProcessing(true);
    setTimeout(() => setIsProcessing(false), 4000);
  };

  const simulateAnalysis = () => {
    setIsAnalyzing(true);
    setTimeout(() => setIsAnalyzing(false), 5000);
  };

  return (
    <PlaygroundPageLayout>
      <div className="space-y-8">
        
        {/* Basic Usage */}
        <div>
          <h3 className="text-xl font-semibold text-text-primary mb-4">Basic Usage</h3>
          <Card>
            <CardBody>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
                <div className="flex flex-col items-center space-y-3">
                  <Spinner />
                  <h4 className="text-sm font-medium text-text-secondary">Default Spinner</h4>
                </div>
                
                <div className="flex flex-col items-center space-y-3">
                  <Spinner label="Loading..." />
                  <h4 className="text-sm font-medium text-text-secondary">With Label</h4>
                </div>
                
                <div className="flex flex-col items-center space-y-3">
                  <Spinner color="secondary" />
                  <h4 className="text-sm font-medium text-text-secondary">Colored Spinner</h4>
                </div>
              </div>
            </CardBody>
          </Card>
        </div>

        {/* Sizes */}
        <div>
          <h3 className="text-xl font-semibold text-text-primary mb-4">Sizes</h3>
          <Card>
            <CardBody>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
                <div className="flex flex-col items-center space-y-3">
                  <Spinner size="sm" color="primary" />
                  <h4 className="text-sm font-medium text-text-secondary">Small</h4>
                </div>
                
                <div className="flex flex-col items-center space-y-3">
                  <Spinner size="md" color="primary" />
                  <h4 className="text-sm font-medium text-text-secondary">Medium (Default)</h4>
                </div>
                
                <div className="flex flex-col items-center space-y-3">
                  <Spinner size="lg" color="primary" />
                  <h4 className="text-sm font-medium text-text-secondary">Large</h4>
                </div>
              </div>
            </CardBody>
          </Card>
        </div>

        {/* Colors */}
        <div>
          <h3 className="text-xl font-semibold text-text-primary mb-4">Colors</h3>
          <Card>
            <CardBody>
              <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-6">
                <div className="flex flex-col items-center space-y-3">
                  <Spinner color="default" />
                  <h4 className="text-sm font-medium text-text-secondary">Default</h4>
                </div>
                
                <div className="flex flex-col items-center space-y-3">
                  <Spinner color="primary" />
                  <h4 className="text-sm font-medium text-text-secondary">Primary</h4>
                </div>
                
                <div className="flex flex-col items-center space-y-3">
                  <Spinner color="secondary" />
                  <h4 className="text-sm font-medium text-text-secondary">Secondary</h4>
                </div>
                
                <div className="flex flex-col items-center space-y-3">
                  <Spinner color="success" />
                  <h4 className="text-sm font-medium text-text-secondary">Success</h4>
                </div>
                
                <div className="flex flex-col items-center space-y-3">
                  <Spinner color="warning" />
                  <h4 className="text-sm font-medium text-text-secondary">Warning</h4>
                </div>
                
                <div className="flex flex-col items-center space-y-3">
                  <Spinner color="danger" />
                  <h4 className="text-sm font-medium text-text-secondary">Danger</h4>
                </div>
              </div>
            </CardBody>
          </Card>
        </div>

        {/* Variants */}
        <div>
          <h3 className="text-xl font-semibold text-text-primary mb-4">Variants</h3>
          <Card>
            <CardBody>
              <div className="grid grid-cols-2 md:grid-cols-3 gap-8">
                <div className="flex flex-col items-center space-y-3">
                  <Spinner variant="default" color="primary" />
                  <h4 className="text-sm font-medium text-text-secondary">Default</h4>
                </div>
                
                <div className="flex flex-col items-center space-y-3">
                  <Spinner variant="simple" color="secondary" />
                  <h4 className="text-sm font-medium text-text-secondary">Simple</h4>
                </div>
                
                <div className="flex flex-col items-center space-y-3">
                  <Spinner variant="gradient" color="success" />
                  <h4 className="text-sm font-medium text-text-secondary">Gradient</h4>
                </div>
                
                <div className="flex flex-col items-center space-y-3">
                  <Spinner variant="wave" color="warning" />
                  <h4 className="text-sm font-medium text-text-secondary">Wave</h4>
                </div>
                
                <div className="flex flex-col items-center space-y-3">
                  <Spinner variant="dots" color="danger" />
                  <h4 className="text-sm font-medium text-text-secondary">Dots</h4>
                </div>
                
                <div className="flex flex-col items-center space-y-3">
                  <Spinner variant="spinner" color="primary" />
                  <h4 className="text-sm font-medium text-text-secondary">Spinner</h4>
                </div>
              </div>
            </CardBody>
          </Card>
        </div>

        {/* Labels & Label Colors */}
        <div>
          <h3 className="text-xl font-semibold text-text-primary mb-4">Labels & Label Colors</h3>
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            
            {/* With Labels */}
            <Card>
              <CardHeader>
                <h4 className="text-lg font-semibold text-text-primary">With Labels</h4>
              </CardHeader>
              <CardBody>
                <div className="space-y-6">
                  <div className="flex items-center space-x-4">
                    <Spinner size="sm" color="primary" label="Loading data..." />
                  </div>
                  
                  <div className="flex items-center space-x-4">
                    <Spinner size="md" color="success" label="Processing trades..." />
                  </div>
                  
                  <div className="flex items-center space-x-4">
                    <Spinner size="lg" color="warning" label="Analyzing portfolio..." />
                  </div>
                </div>
              </CardBody>
            </Card>

            {/* Custom Label Colors */}
            <Card>
              <CardHeader>
                <h4 className="text-lg font-semibold text-text-primary">Custom Label Colors</h4>
              </CardHeader>
              <CardBody>
                <div className="space-y-6">
                  <div className="flex items-center space-x-4">
                    <Spinner 
                      color="primary" 
                      label="Processing..." 
                      labelColor="secondary"
                    />
                  </div>
                  
                  <div className="flex items-center space-x-4">
                    <Spinner 
                      color="success" 
                      label="Syncing data..." 
                      labelColor="warning"
                    />
                  </div>
                  
                  <div className="flex items-center space-x-4">
                    <Spinner 
                      color="default" 
                      label="Critical task..." 
                      labelColor="danger"
                    />
                  </div>
                </div>
              </CardBody>
            </Card>
          </div>
        </div>

        {/* Interactive Spinners */}
        <div>
          <h3 className="text-xl font-semibold text-text-primary mb-4">Interactive Examples</h3>
          <Card>
            <CardBody>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
                <div className="flex flex-col items-center space-y-4">
                  {isLoading ? (
                    <Spinner 
                      color="primary" 
                      label="Loading markets..." 
                      variant="gradient"
                    />
                  ) : (
                    <div className="w-8 h-8 flex items-center justify-center">
                      <div className="w-4 h-4 bg-success rounded-full"></div>
                    </div>
                  )}
                  <Button 
                    onPress={simulateLoading}
                    color="primary"
                    size="sm"
                    isDisabled={isLoading}
                  >
                    {isLoading ? 'Loading...' : 'Load Markets'}
                  </Button>
                </div>
                
                <div className="flex flex-col items-center space-y-4">
                  {isProcessing ? (
                    <Spinner 
                      color="secondary" 
                      label="Processing trades..." 
                      variant="dots"
                    />
                  ) : (
                    <div className="w-8 h-8 flex items-center justify-center">
                      <div className="w-4 h-4 bg-success rounded-full"></div>
                    </div>
                  )}
                  <Button 
                    onPress={simulateProcessing}
                    color="secondary"
                    size="sm"
                    isDisabled={isProcessing}
                  >
                    {isProcessing ? 'Processing...' : 'Process Trades'}
                  </Button>
                </div>
                
                <div className="flex flex-col items-center space-y-4">
                  {isAnalyzing ? (
                    <Spinner 
                      color="warning" 
                      label="Analyzing performance..." 
                      variant="wave"
                    />
                  ) : (
                    <div className="w-8 h-8 flex items-center justify-center">
                      <div className="w-4 h-4 bg-success rounded-full"></div>
                    </div>
                  )}
                  <Button 
                    onPress={simulateAnalysis}
                    color="warning"
                    size="sm"
                    isDisabled={isAnalyzing}
                  >
                    {isAnalyzing ? 'Analyzing...' : 'Analyze Performance'}
                  </Button>
                </div>
              </div>
            </CardBody>
          </Card>
        </div>

        {/* Trading Context Examples */}
        <div>
          <h3 className="text-xl font-semibold text-text-primary mb-4">Trading Context</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            
            <Card>
              <CardHeader>
                <h4 className="text-lg font-semibold text-text-primary">Market Operations</h4>
              </CardHeader>
              <CardBody>
                <div className="space-y-6">
                  <div className="flex items-center justify-between p-3 bg-background rounded border">
                    <div>
                      <div className="font-medium text-text-primary">Market Data Feed</div>
                      <div className="text-sm text-text-secondary">Real-time updates</div>
                    </div>
                    <Spinner size="sm" color="success" variant="simple" />
                  </div>
                  
                  <div className="flex items-center justify-between p-3 bg-background rounded border">
                    <div>
                      <div className="font-medium text-text-primary">Trade Execution</div>
                      <div className="text-sm text-text-secondary">Processing order</div>
                    </div>
                    <Spinner size="sm" color="warning" variant="dots" />
                  </div>
                  
                  <div className="flex items-center justify-between p-3 bg-background rounded border">
                    <div>
                      <div className="font-medium text-text-primary">Risk Calculation</div>
                      <div className="text-sm text-text-secondary">Updating positions</div>
                    </div>
                    <Spinner size="sm" color="primary" variant="gradient" />
                  </div>
                </div>
              </CardBody>
            </Card>

            <Card>
              <CardHeader>
                <h4 className="text-lg font-semibold text-text-primary">Background Tasks</h4>
              </CardHeader>
              <CardBody>
                <div className="space-y-6">
                  <div className="flex items-center justify-between p-3 bg-background rounded border">
                    <div>
                      <div className="font-medium text-text-primary">Portfolio Sync</div>
                      <div className="text-sm text-text-secondary">Syncing with broker</div>
                    </div>
                    <Spinner size="sm" color="secondary" variant="wave" />
                  </div>
                  
                  <div className="flex items-center justify-between p-3 bg-background rounded border">
                    <div>
                      <div className="font-medium text-text-primary">Report Generation</div>
                      <div className="text-sm text-text-secondary">Creating monthly report</div>
                    </div>
                    <Spinner size="sm" color="primary" variant="spinner" />
                  </div>
                  
                  <div className="flex items-center justify-between p-3 bg-background rounded border">
                    <div>
                      <div className="font-medium text-text-primary">Data Backup</div>
                      <div className="text-sm text-text-secondary">Backing up trades</div>
                    </div>
                    <Spinner size="sm" color="default" />
                  </div>
                </div>
              </CardBody>
            </Card>
          </div>
        </div>

        {/* Loading States */}
        <div>
          <h3 className="text-xl font-semibold text-text-primary mb-4">Loading States</h3>
          <Card>
            <CardBody>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
                <div className="text-center space-y-3">
                  <Spinner color="primary" size="lg" variant="gradient" />
                  <div>
                    <div className="font-medium text-text-primary">Loading Dashboard</div>
                    <div className="text-sm text-text-secondary">Please wait...</div>
                  </div>
                </div>
                
                <div className="text-center space-y-3">
                  <Spinner color="success" size="md" variant="dots" />
                  <div>
                    <div className="font-medium text-text-primary">Fetching Trades</div>
                    <div className="text-sm text-text-secondary">Retrieving data...</div>
                  </div>
                </div>
                
                <div className="text-center space-y-3">
                  <Spinner color="warning" size="md" variant="wave" />
                  <div>
                    <div className="font-medium text-text-primary">Processing</div>
                    <div className="text-sm text-text-secondary">Almost done...</div>
                  </div>
                </div>
                
                <div className="text-center space-y-3">
                  <Spinner color="secondary" size="sm" variant="simple" />
                  <div>
                    <div className="font-medium text-text-primary">Initializing</div>
                    <div className="text-sm text-text-secondary">Starting up...</div>
                  </div>
                </div>
              </div>
            </CardBody>
          </Card>
        </div>

        {/* Inline Spinners */}
        <div>
          <h3 className="text-xl font-semibold text-text-primary mb-4">Inline Usage</h3>
          <Card>
            <CardBody>
              <div className="space-y-4">
                <div className="flex items-center space-x-3">
                  <Spinner size="sm" color="primary" />
                  <span className="text-text-primary">Loading portfolio data...</span>
                </div>
                
                <div className="flex items-center space-x-3">
                  <span className="text-text-primary">Processing trades</span>
                  <Spinner size="sm" color="success" variant="dots" />
                </div>
                
                <div className="flex items-center space-x-3">
                  <span className="text-text-primary">Calculating risk metrics</span>
                  <Spinner size="sm" color="warning" variant="simple" />
                  <span className="text-text-secondary">This may take a moment...</span>
                </div>
                
                <div className="p-4 bg-background border border-border rounded">
                  <div className="flex items-center space-x-3">
                    <Spinner size="sm" color="secondary" variant="gradient" />
                    <div>
                      <div className="font-medium text-text-primary">Syncing with Interactive Brokers</div>
                      <div className="text-sm text-text-secondary">Fetching latest positions and balances...</div>
                    </div>
                  </div>
                </div>
              </div>
            </CardBody>
          </Card>
        </div>

      </div>
    </PlaygroundPageLayout>
  );
};